import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContactButton from "../components/FloatingContactButton";
import { useTranslation } from "react-i18next";
import {
  sendClientConfirmationEmail,
  sendBusinessNotificationEmail,
} from "../services/emailService";

const Contact = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFacebookLanding = location.pathname === "/contactfb";
  const netlifyFormName = isFacebookLanding ? "contactfb" : "contact";
  const [isVisible, setIsVisible] = useState({
    header: false,
    form: false,
  });

  const [submissionStatus, setSubmissionStatus] = useState({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    errorMessage: "",
  });

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    postalCode: "",
    services: {
      lawnMowing: false,
      hedgeTrimming: false,
      cleanups: false,
      aeration: false,
      fertilization: false,
      topdressing: false,
      treePruning: false,
      sodding: false,
      delivery: false,
      exteriorPainting: false,
      pressureWashing: false,
      eavestrough: false,
      transportation: false,
      landscaping: false,
      paveUni: false,
      excavation: false,
      snowRemoval: false,
    },
    specialRequest: "",
    marketingConsent: false,
  });

  // Animation when scrolling
  useEffect(() => {
    const handleScroll = () => {
      const headerSection = document.getElementById("header-section");
      const formSection = document.getElementById("form-section");

      if (headerSection) {
        setIsVisible((prev) => ({ ...prev, header: true }));
      }

      if (
        formSection &&
        window.scrollY > formSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, form: true }));
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once to set initial visibility
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isFacebookLanding) return;
    const w = window as Window & {
      fbq?: (...args: unknown[]) => void;
      dataLayer?: Record<string, unknown>[];
    };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "facebook_contact_landing",
      page_path: "/contactfb",
    });
    if (typeof w.fbq === "function") {
      w.fbq("trackCustom", "FacebookContactFormView");
    }
  }, [isFacebookLanding]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checkbox = e.target as HTMLInputElement;
      if (name === "services[]") {
        // Get the service name from the value attribute
        const serviceName = checkbox.value;
        setFormData((prev) => ({
          ...prev,
          services: {
            ...prev.services,
            [serviceName]: checkbox.checked,
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that at least one service is selected
    const selectedServices = Object.values(formData.services).some(
      (service) => service === true
    );
    if (!selectedServices) {
      setSubmissionStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: t("contact.form.selectService"),
      });
      setTimeout(() => {
        setSubmissionStatus((prev) => ({
          ...prev,
          isError: false,
          errorMessage: "",
        }));
      }, 5000);
      return;
    }

    setSubmissionStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      errorMessage: "",
    });

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSend = new FormData(form);

      // Add selected services as a readable string for better email formatting
      const selectedServicesList = Object.entries(formData.services)
        .filter(([_, isSelected]) => isSelected)
        .map(([serviceName]) => {
          // Convert camelCase to readable format
          return serviceName
            .replace(/([A-Z])/g, " $1")
            .toLowerCase()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        })
        .join(", ");

      formDataToSend.append("selected-services-readable", selectedServicesList);

      // Submit to Netlify Forms (existing functionality)
      const netlifyResponse = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (!netlifyResponse.ok) {
        throw new Error("Form submission failed");
      }

      const leadEmailOptions = {
        leadSourceLabel: isFacebookLanding
          ? t("contact.confirmationEmail.fromFacebookAd")
          : undefined,
      };

      // After Netlify accepts the submission: notify submitter + optional business inbox (EmailJS)
      const emailSent = await sendClientConfirmationEmail(
        formData,
        leadEmailOptions
      );
      await sendBusinessNotificationEmail(formData, leadEmailOptions);

      setSubmissionStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        errorMessage: "",
      });

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        address: "",
        postalCode: "",
        services: {
          lawnMowing: false,
          hedgeTrimming: false,
          cleanups: false,
          aeration: false,
          fertilization: false,
          topdressing: false,
          treePruning: false,
          sodding: false,
          delivery: false,
          exteriorPainting: false,
          pressureWashing: false,
          eavestrough: false,
          transportation: false,
          landscaping: false,
          paveUni: false,
          excavation: false,
          snowRemoval: false,
        },
        specialRequest: "",
        marketingConsent: false,
      });

      // Hide success message after 8 seconds
      setTimeout(() => {
        setSubmissionStatus((prev) => ({ ...prev, isSuccess: false }));
      }, 8000);

      // Log email status (for debugging)
      if (emailSent) {
        console.log("Confirmation email sent successfully");
      } else {
        console.log(
          "Form submitted successfully, but confirmation email failed to send"
        );
      }

      const w = window as Window & { fbq?: (...args: unknown[]) => void };
      if (isFacebookLanding && typeof w.fbq === "function") {
        w.fbq("track", "Lead");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmissionStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: t("contact.form.error"),
      });

      // Hide error message after 5 seconds
      setTimeout(() => {
        setSubmissionStatus((prev) => ({
          ...prev,
          isError: false,
          errorMessage: "",
        }));
      }, 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header Section */}
      <section
        id="header-section"
        className={`relative py-24 bg-primary-800 transition-opacity duration-1000 ease-in-out ${
          isVisible.header ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            {t("contact.title")}
          </h1>
          <div className="w-24 h-1.5 bg-primary-400 mx-auto rounded-full mb-6"></div>
          <p className="text-xl text-white max-w-3xl mx-auto">
            {t("contact.subtitle")}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section
        id="form-section"
        className={`py-16 bg-white transition-all duration-1000 ease-in-out ${
          isVisible.form
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden p-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6 text-center">
              {t("contact.form.title")}
            </h2>

            {/* Success Message */}
            {submissionStatus.isSuccess && (
              <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-400 rounded-r-lg animate-fade-in">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      {t("contact.form.successTitle")}
                    </h3>
                    <div className="mt-1 text-sm text-green-700">
                      <p>{t("contact.form.successDescription")}</p>
                    </div>
                  </div>
                  <div className="ml-auto pl-3">
                    <div className="-mx-1.5 -my-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          setSubmissionStatus((prev) => ({
                            ...prev,
                            isSuccess: false,
                          }))
                        }
                        className="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-50 focus:ring-green-600"
                      >
                        <span className="sr-only">
                          {t("contact.form.dismiss")}
                        </span>
                        <svg
                          className="h-3 w-3"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submissionStatus.isError && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg animate-fade-in">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("contact.form.errorTitle")}
                    </h3>
                    <div className="mt-1 text-sm text-red-700">
                      <p>{submissionStatus.errorMessage}</p>
                    </div>
                  </div>
                  <div className="ml-auto pl-3">
                    <div className="-mx-1.5 -my-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          setSubmissionStatus((prev) => ({
                            ...prev,
                            isError: false,
                            errorMessage: "",
                          }))
                        }
                        className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                      >
                        <span className="sr-only">
                          {t("contact.form.dismiss")}
                        </span>
                        <svg
                          className="h-3 w-3"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form
              name={netlifyFormName}
              method="post"
              action="/"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <input type="hidden" name="form-name" value={netlifyFormName} />
              {isFacebookLanding ? (
                <input type="hidden" name="traffic-source" value="facebook" />
              ) : null}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-neutral-700 font-medium mb-2"
                  >
                    {t("contact.form.name")} *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder={t("contact.form.name")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-neutral-700 font-medium mb-2"
                  >
                    {t("contact.form.email")} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder={t("contact.form.email")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-neutral-700 font-medium mb-2"
                  >
                    {t("contact.form.phone")} *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder={t("contact.form.phone")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="address"
                    className="block text-neutral-700 font-medium mb-2"
                  >
                    {t("contact.form.address")} *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder={t("contact.form.address")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="postalCode"
                    className="block text-neutral-700 font-medium mb-2"
                  >
                    {t("contact.form.postalCode")} *
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder={t("contact.form.postalCode")}
                  />
                </div>
              </div>

              <div>
                <label className="block text-neutral-700 font-medium mb-4">
                  {t("contact.form.service")} *
                </label>
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="lawnMowing"
                      name="services[]"
                      value="lawnMowing"
                      checked={formData.services.lawnMowing}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                    />
                    <label
                      htmlFor="lawnMowing"
                      className="ml-2 text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.lawnMowing")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="hedgeTrimming"
                      name="services[]"
                      value="hedgeTrimming"
                      checked={formData.services.hedgeTrimming}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="hedgeTrimming"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.hedgeTrimming")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="cleanups"
                      name="services[]"
                      value="cleanups"
                      checked={formData.services.cleanups}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="cleanups"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.cleanups")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="aeration"
                      name="services[]"
                      value="aeration"
                      checked={formData.services.aeration}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="aeration"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.aeration")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="fertilization"
                      name="services[]"
                      value="fertilization"
                      checked={formData.services.fertilization}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="fertilization"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.fertilization")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="topdressing"
                      name="services[]"
                      value="topdressing"
                      checked={formData.services.topdressing}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="topdressing"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.topdressing")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="treePruning"
                      name="services[]"
                      value="treePruning"
                      checked={formData.services.treePruning}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="treePruning"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.treePruning")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="sodding"
                      name="services[]"
                      value="sodding"
                      checked={formData.services.sodding}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="sodding"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.sodding")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="delivery"
                      name="services[]"
                      value="delivery"
                      checked={formData.services.delivery}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="delivery"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.delivery")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="exteriorPainting"
                      name="services[]"
                      value="exteriorPainting"
                      checked={formData.services.exteriorPainting}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="exteriorPainting"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.exteriorPainting")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="pressureWashing"
                      name="services[]"
                      value="pressureWashing"
                      checked={formData.services.pressureWashing}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="pressureWashing"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.pressureWashing")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="eavestrough"
                      name="services[]"
                      value="eavestrough"
                      checked={formData.services.eavestrough}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="eavestrough"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.eavestrough")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="transportation"
                      name="services[]"
                      value="transportation"
                      checked={formData.services.transportation}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="transportation"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.transportation")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="landscaping"
                      name="services[]"
                      value="landscaping"
                      checked={formData.services.landscaping}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="landscaping"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.landscaping")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="paveUni"
                      name="services[]"
                      value="paveUni"
                      checked={formData.services.paveUni}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="paveUni"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.paveUni")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="excavation"
                      name="services[]"
                      value="excavation"
                      checked={formData.services.excavation}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="excavation"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.excavation")}
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="snowRemoval"
                      name="services[]"
                      value="snowRemoval"
                      checked={formData.services.snowRemoval}
                      onChange={handleChange}
                      className="h-5 w-5 text-primary-600 rounded mt-1"
                    />
                    <label
                      htmlFor="snowRemoval"
                      className="ml-2 block text-sm text-neutral-700"
                    >
                      {t("contact.form.serviceOptions.snowRemoval")}
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="specialRequest"
                  className="block text-neutral-700 font-medium mb-2"
                >
                  {t("contact.form.message")}
                </label>
                <textarea
                  id="specialRequest"
                  name="specialRequest"
                  value={formData.specialRequest}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-32"
                  placeholder={t("contact.form.message")}
                ></textarea>
              </div>

              {/* Netlify bot field - do not remove */}
              <div className="hidden">
                <label>
                  Don't fill this out if you're human:{" "}
                  <input name="bot-field" />
                </label>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={submissionStatus.isSubmitting}
                  className={`inline-flex items-center justify-center bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg px-8 py-4 transition-colors duration-300 shadow-md ${
                    submissionStatus.isSubmitting
                      ? "opacity-75 cursor-not-allowed"
                      : "hover:shadow-lg transform hover:-translate-y-0.5"
                  }`}
                >
                  {submissionStatus.isSubmitting && (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {submissionStatus.isSubmitting
                    ? t("contact.form.submitting")
                    : t("contact.form.submit")}
                </button>
              </div>
            </form>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            <a
              href="tel:514-123-4567"
              className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {t("contact.contactInfo.phone")}
              </h3>
              <p className="text-neutral-600">
                {t("contact.contactInfo.phoneNumber")}
              </p>
            </a>

            <a
              href="mailto:mattellerton.landscaping@gmail.com"
              className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {t("contact.contactInfo.email")}
              </h3>
              <p className="text-neutral-600 break-all text-sm px-5">
                {t("contact.contactInfo.emailAddress")}
              </p>
            </a>

            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {t("contact.contactInfo.location")}
              </h3>
              <p className="text-neutral-600">
                {t("contact.contactInfo.address")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButton />
    </div>
  );
};

export default Contact;
